/**
 * Super video
 * - Custom video control pill: play/pause the section video, with a label and
 *   a m:ss time read-out (duration when idle, elapsed while playing).
 * - Slides carousel: a self-contained scroll-snap slider for the Slides group
 *   theme block. Builds one dot per slide (so the count always matches the
 *   blocks Shopify rendered), wires prev/next + dots, optional autoplay, and
 *   keeps the active dot in sync when the user free-scrolls/swipes.
 * Works without JS too: the slides degrade to a horizontal scroller.
 */
(function () {
  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '';
    var mins = Math.floor(seconds / 60);
    var secs = Math.floor(seconds % 60);
    return mins + ':' + (secs < 10 ? '0' + secs : secs);
  }

  function initVideoControl(section) {
    var pill = section.querySelector('[data-video-control]');
    var video = section.querySelector('.super-video__video');
    if (!pill || !video) return;

    var toggle = pill.querySelector('[data-video-toggle]');
    var timeEl = pill.querySelector('[data-video-time]');
    if (!toggle) return;

    function showDuration() {
      if (timeEl) timeEl.textContent = formatTime(video.duration);
    }

    // Metadata may already be loaded by the time JS runs.
    if (video.readyState >= 1) showDuration();
    video.addEventListener('loadedmetadata', showDuration);

    video.addEventListener('timeupdate', function () {
      if (timeEl && !video.paused) timeEl.textContent = formatTime(video.currentTime);
    });

    video.addEventListener('play', function () {
      pill.classList.add('is-playing');
      toggle.setAttribute('aria-pressed', 'true');
    });

    video.addEventListener('pause', function () {
      pill.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      showDuration();
    });

    video.addEventListener('ended', function () {
      pill.classList.remove('is-playing');
      toggle.setAttribute('aria-pressed', 'false');
      showDuration();
    });

    // Reflect the initial state (autoplay videos may already be playing).
    if (!video.paused && !video.ended) {
      pill.classList.add('is-playing');
      toggle.setAttribute('aria-pressed', 'true');
    }

    toggle.addEventListener('click', function () {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    });
  }

  function initSlider(root) {
    var track = root.querySelector('[data-super-slider-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-super-slide]'));
    if (slides.length < 2) return; // nothing to rotate through

    var dotsWrap = root.querySelector('[data-super-dots]');
    var prevBtn = root.querySelector('[data-super-prev]');
    var nextBtn = root.querySelector('[data-super-next]');

    var current = 0;
    var dots = [];

    // Build one dot per slide so the count always matches what content_for rendered.
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'super-video__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () {
          goTo(i, true);
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    function setActive(index) {
      current = index;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    function goTo(index, fromUser) {
      var count = slides.length;
      index = ((index % count) + count) % count;
      var slide = slides[index];
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      setActive(index);
      if (fromUser) restartAutoplay();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(current - 1, true);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(current + 1, true);
      });
    }

    // Keep the active dot synced when the user swipes/free-scrolls the track.
    var scrollTimer;
    track.addEventListener('scroll', function () {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        var nearest = 0;
        var min = Infinity;
        slides.forEach(function (slide, i) {
          var dist = Math.abs(slide.offsetLeft - track.offsetLeft - track.scrollLeft);
          if (dist < min) {
            min = dist;
            nearest = i;
          }
        });
        if (nearest !== current) setActive(nearest);
      }, 100);
    });

    // --- Optional autoplay ---
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var speed = (parseInt(root.getAttribute('data-speed'), 10) || 5) * 1000;
    var timer = null;

    function startAutoplay() {
      if (!autoplay || timer) return;
      timer = window.setInterval(function () {
        if (!document.hidden) goTo(current + 1, false);
      }, speed);
    }

    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    setActive(0);
    startAutoplay();

    // Let the theme editor jump to a slide when its block is selected.
    root._superSliderGoTo = goTo;
    root._superSliderSlides = slides;
  }

  function initSection(section) {
    if (!section || section.dataset.svInit === 'true') return;
    section.dataset.svInit = 'true';
    initVideoControl(section);
    section.querySelectorAll('[data-super-slider]').forEach(initSlider);
  }

  function initAll() {
    document.querySelectorAll('.super-video[data-section-id]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: the section markup is replaced on reload, so reset the init
  // flag before re-initialising (otherwise dots would be rebuilt twice).
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.super-video[data-section-id]');
    if (section) {
      section.dataset.svInit = '';
      initSection(section);
    }
  });

  // Theme editor: advance the carousel to the slide whose block is selected.
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest('[data-super-slide]');
    if (!slide) return;
    var root = slide.closest('[data-super-slider]');
    if (root && typeof root._superSliderGoTo === 'function' && root._superSliderSlides) {
      var index = root._superSliderSlides.indexOf(slide);
      if (index > -1) root._superSliderGoTo(index, true);
    }
  });
})();
